import { useContext, useDebugValue, useEffect, useState } from "react"
import { CheckBoxSquare, DateInput, DropdownMenu, EditBtn, RadioCheckButton, SaveCancelBox, TextInput, TextInputMultiline, AgeOfChildrenFamSelect, SafeEditBtn } from "./Common"
import "./Wishes.css"
import { LocaleContext, LoginContext, OverlayContext, ScrollContext, StateContext } from "./App"
import { genId, get_translation_objects, postWithCsrf } from "./util";
import { AgeOfChildren, AvailabilityByPhone, AvailabilityTime, Gender, GenderNanny, Graduation, LoadState, Location, NoOfChildren, Notifications, Recommendation, Salutation, Start, Title, UserType, WeekDays } from "./enums.mjs";
import { LanguagesSelect } from "./LanguagesSelect";
import { maxCustomerCommentLength, maxLanguagesFamily, maxLanguagesNanny, maxSpecialCareTextLength } from "./globals.mjs";
import { useSafeEdit } from "./hooks";

export const Wishes = ({userDataOrig, setUserDataOrig, onUserDataChange, readOnly, userId, userType, cancelTrg,
                        matchInfo, recalcTrigger, management, lockNavigation}) => {
    const [id, setId] = useState(genId());

    const [editable, setEditable] = useState(false);
    const [userData, setUserData] = useState(userDataOrig);
    const [oldUserData, setOldUserData] = useState(null);
    const [saveState, setSaveState] = useState(LoadState.NONE);

    if(onUserDataChange === undefined) {
        onUserDataChange = (value) => {};
    }

    const locale = useContext(LocaleContext);
    const scroll = useContext(ScrollContext);
    const state = useContext(StateContext);
    const overlay = useContext(OverlayContext);


    // FUNCTIONS:

    function isSavable() {
        if(!editable) {
            return false;
        }

        if(JSON.stringify(userData) === JSON.stringify(oldUserData)) {
            return false;
        }

        return true;
    }

    function setEditableFunc(e) {
        safeEdit.setDisplayEditTooltip(false);
        setEditable(e);
    }

    function handleEdit() {
        if(editable) {
            return;
        }

        setOldUserData(JSON.parse(JSON.stringify(userData)));
        setEditableFunc(true);
        setSaveState(LoadState.NONE);
    }

    function handleSave() {
        const data = {
            start: userData.start,
            start_specific: userData.start_specific,

            availability_time: userData.availability_time,

            week_days: userData.week_days,
            week_days_specific_monday: userData.week_days_specific_monday,
            week_days_specific_tuesday: userData.week_days_specific_tuesday,
            week_days_specific_wednesday: userData.week_days_specific_wednesday,
            week_days_specific_thursday: userData.week_days_specific_thursday,
            week_days_specific_friday: userData.week_days_specific_friday,
            week_days_specific_saturday: userData.week_days_specific_saturday,
            week_days_specific_sunday: userData.week_days_specific_sunday,

            drivers_license: userData.drivers_license,
            drive_foreign_car: userData.drive_foreign_car,
            homework_support: userData.homework_support,
            special_care: userData.special_care,
            special_care_text: userData.special_care_text,
            first_aid_course_for_children: userData.first_aid_course_for_children,
            pets_are_ok: userData.pets_are_ok,
            musically: userData.musically,
            cooking: userData.cooking,

            languages: userData.languages,
            comment: userData.comment
        };

        if(userType === UserType.NANNY) {
            data["no_of_children"] = userData.no_of_children;
            data["age_of_children"] = userData.age_of_children;
            data["age_of_children_specific_1-2"] = userData["age_of_children_specific_1-2"];
            data["age_of_children_specific_3-6"] = userData["age_of_children_specific_3-6"];
            data["age_of_children_specific_>6"] = userData["age_of_children_specific_>6"];

        }
        else {
            data["age_of_children_fam"] = userData.age_of_children_fam;
            data["gender_nanny"] = userData.gender_nanny;
        }

        try {
            postWithCsrf("/user/management/set_user_data_wishes", JSON.stringify({"user_id": userId, "care_data_wishes": data }), state).then(response => {
                if(response.ok) {
                    return response.json();
                }
                else {

                    if(response.status === 520) {
                        overlay.setErrorPopupInfo({"title": locale.msgs.GEOCODING_ERROR, "text": locale.msgs.GEOCODING_ERROR_DESCR})
                        overlay.setErrorPopupDisplayed(true);
                    }

                    throw new Error("Response code: " + response.status);
                }
            }).then(json => {
                setSaveState(LoadState.LOAD_SUCCESS);
                setEditableFunc(false);

                setUserData(json["care_data"]);
                setUserDataOrig(json["care_data"]);
            }).catch(error => {
                console.log(error);
                setSaveState(LoadState.LOAD_ERROR);
            });

            // Calling setSaveState() after postWithCsrf() as it could throw an exception
            setSaveState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    function handleCancel() {
        setEditableFunc(false);
        setUserData(oldUserData);
        setSaveState(LoadState.NONE);
    }

    function setUserDataAndResetSaveState(userData) {
        setUserData(userData);
        setSaveState(LoadState.NONE);
    }


    // EFFECTS:

    useEffect(() => {
        if(editable) {
            handleCancel();
        }
    }, [cancelTrg]);

    useEffect(() => {
        if(!editable) {
            setUserData(userDataOrig);
        }
        
        setOldUserData(userDataOrig);
    }, [userDataOrig]);

    const safeEdit = useSafeEdit(editable, [userData], [oldUserData], lockNavigation);


    // RENDER:

    if(userType === UserType.NANNY) {
        var [noOfChildrenTransList, noOfChildrenTransListInverse] = get_translation_objects("no_of_children_mn", NoOfChildren, locale);
    }
    else if(userType === UserType.FAMILY) {
        var [noOfChildrenTransList, noOfChildrenTransListInverse] = get_translation_objects("no_of_children_fam", NoOfChildren, locale);
    }

    const [ageOfChildrenTransList, ageOfChildrenTransListInverse] = get_translation_objects("age_of_children", AgeOfChildren, locale);
    const [availabilityTimeTransList, availabilityTimeTransListInverse] = get_translation_objects("availability", AvailabilityTime, locale);
    const [weekDaysTransList, weekDaysTransListInverse] = get_translation_objects("week_days", WeekDays, locale);
    const [startTransList, startTransListInverse] = get_translation_objects("start_brokerage", Start, locale);
    const [genderTransList, genderTransListInverse] = get_translation_objects("gender", GenderNanny, locale);

    return (
        <div className="job_wishes_box tab_switch_paading">
            { !readOnly ?
                <SafeEditBtn safeEdit={safeEdit} editable={editable} onClick={handleEdit} />
            :
                null
            }

            <div ref={safeEdit.editBoxRef} className="job_wishes_content input_minimal_gap">

                {/* NO_OF_CHILDREN */}
                
                { userType === UserType.NANNY ?
                    <MatchInfoWrapper points={matchInfo ? matchInfo["no_of_children"] : null}>
                        <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.NO_OF_CHILDREN} options={Object.values(noOfChildrenTransList)}
                                value={noOfChildrenTransList[userData["no_of_children"]]}
                                onValueChange={(value) => setUserDataAndResetSaveState({...userData, "no_of_children": noOfChildrenTransListInverse[value]})}
                                onScroll={scroll.scrollTo} />
                    </MatchInfoWrapper>
                :
                    null
                }

                { userType === UserType.FAMILY && !editable ?
                    <MatchInfoWrapper points={matchInfo ? matchInfo["no_of_children"] : null}>
                        <TextInput minimal={true} readOnly={!editable} text={locale.msgs.NO_OF_CHILDREN}
                                value={userData["age_of_children_fam"].length} onChange={() => {}} />
                    </MatchInfoWrapper>
                :
                    null
                }


                {/* AGE_OF_CHILDREN */}

                { userType === UserType.NANNY ?
                    <div className="input_minimal_gap flex_col">
                        {
                            !editable && userData["age_of_children"] === AgeOfChildren.SPECIFIC ?
                                null
                            :
                                <MatchInfoWrapper points={matchInfo ? matchInfo["age_of_children"] : null}>
                                    <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.AGE_OF_CHILDREN} options={Object.values(ageOfChildrenTransList)}
                                            value={ageOfChildrenTransList[userData["age_of_children"]]}
                                            onValueChange={(value) => setUserDataAndResetSaveState({...userData, "age_of_children": ageOfChildrenTransListInverse[value]})}
                                            onScroll={scroll.scrollTo} />
                                </MatchInfoWrapper>
                        }

                        {
                            userData["age_of_children"] === AgeOfChildren.SPECIFIC ?
                                <div className="job_wishes_specific_box">

                                    <div className="job_wishes_specific_head">
                                        <MatchInfoWrapper points={matchInfo ? matchInfo["age_of_children"] : null}>
                                            <div className="text_light_2">{!editable ? locale.msgs.AGE_OF_CHILDREN : locale.msgs.AGE_OF_CHILDREN_SPECIFIC_2}</div>
                                        </MatchInfoWrapper>
                                    </div>

                                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.ONE_TO_TWO_YEARS}
                                            id={"age_specific_0_" + id} name={"age_specific_0_" + id}
                                            checked={userData["age_of_children_specific_1-2"]}
                                            onChange={(e) => setUserDataAndResetSaveState({...userData, "age_of_children_specific_1-2": e.target.checked})}/>
                                
                                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.THREE_TO_SIX_YEARS}
                                            id={"age_specific_1_" + id} name={"age_specific_1_" + id}
                                            checked={userData["age_of_children_specific_3-6"]}
                                            onChange={(e) => setUserDataAndResetSaveState({...userData, "age_of_children_specific_3-6": e.target.checked})}/>
                        
                                
                                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.MORE_THAN_SEVEN_YEARS}
                                            id={"age_specific_2_" + id} name={"age_specific_2_" + id}
                                            checked={userData["age_of_children_specific_>6"]}
                                            onChange={(e) => setUserDataAndResetSaveState({...userData, "age_of_children_specific_>6": e.target.checked})}/>
                                </div>
                            :
                                null
                        }
                    </div>
                :
                    null
                }


                {/* AGE_OF_CHILDREN_FAM */}

                { userType === UserType.FAMILY ?
                    <div>
                        {/*<div className="job_wishes_specific_head">
                            <MatchInfoWrapper points={matchInfo ? matchInfo["age_of_children"] : null}>
                                <div className="text_light_2">{!editable ? locale.msgs.AGE_OF_CHILDREN : locale.msgs.CHILDREN}</div>
                            </MatchInfoWrapper>
                        </div>*/}
                        
                        <AgeOfChildrenFamSelect editable={editable} minimal={true} management={true} ageOfChildrenFam={userData["age_of_children_fam"]}
                                onAgeOfChildrenFamChange={(v) => setUserDataAndResetSaveState({...userData, "age_of_children_fam": v}) }
                                points={matchInfo ? matchInfo["age_of_children"] : null} />
                    </div>
                :
                    null
                }


                {/* GENDER */}

                { userType == UserType.FAMILY ?
                    <MatchInfoWrapper points={matchInfo ? matchInfo["gender_nanny"] : null}>
                        <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.GENDER_WISHED} options={Object.values(genderTransList)}
                                value={genderTransList[userData["gender_nanny"]]}
                                onValueChange={(value) => setUserDataAndResetSaveState({...userData, "gender_nanny": genderTransListInverse[value]})}
                                onScroll={scroll.scrollTo} />
                    </MatchInfoWrapper>
                :
                    null
                }


                {/* START */}

                <MatchInfoWrapper points={matchInfo ? matchInfo["start_info"] : null}>

                    {
                        !editable ?
                            userData["start"] === Start.ASAP ?
                                <TextInput minimal={true} readOnly={!editable} text={locale.msgs.START_BROKERAGE}
                                        value={startTransList[Start.ASAP]} onChange={() => {}} />
                            :
                                <DateInput minimal={true} readOnly={!editable} text={locale.msgs.START_BROKERAGE} value={userData["start_specific"]}
                                        onChange={(value) => setUserDataAndResetSaveState({...userData, "start_specific": value})} />
                        :
                            <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.START_BROKERAGE} options={Object.values(startTransList)}
                                    value={startTransList[userData["start"]]}
                                    onValueChange={(value) => setUserDataAndResetSaveState({...userData, "start": startTransListInverse[value]})}
                                    onScroll={scroll.scrollTo} />
                    }

                    {
                        editable && userData["start"] === Start.SPECIFIC ?
                        
                            <DateInput minimal={true} readOnly={!editable} text={locale.msgs.START_BROKERAGE_SPECIFIC_2} value={userData["start_specific"]}
                                    onChange={(value) => setUserDataAndResetSaveState({...userData, "start_specific": value})} />
                        :
                            null
                    }

                </MatchInfoWrapper>


                {/* AVAILABILITY */}

                <MatchInfoWrapper points={matchInfo ? matchInfo["availability_time"] : null}>
                    <DropdownMenu minimal={true} readOnly={!editable} text={ userType === UserType.NANNY ? locale.msgs.AVAILABILITY : locale.msgs.NEED}
                            options={Object.values(availabilityTimeTransList)}
                            value={availabilityTimeTransList[userData["availability_time"]]}
                            onValueChange={(value) => setUserDataAndResetSaveState({...userData, "availability_time": availabilityTimeTransListInverse[value]})}
                            onScroll={scroll.scrollTo} />
                </MatchInfoWrapper>


                {/* WEEK_DAYS */}

                {
                    !editable && userData["week_days"] === WeekDays.SPECIFIC ?
                        null
                    :
                        <MatchInfoWrapper points={matchInfo ? matchInfo["availability_week_days"] : null}>
                            <DropdownMenu minimal={true} readOnly={!editable} text={locale.msgs.WEEK_DAYS} options={Object.values(weekDaysTransList)}
                                    value={weekDaysTransList[userData["week_days"]]}
                                    onValueChange={(value) => setUserDataAndResetSaveState({...userData, "week_days": weekDaysTransListInverse[value]})}
                                    onScroll={scroll.scrollTo} />
                        </MatchInfoWrapper>
                }

                {
                    userData["week_days"] === WeekDays.SPECIFIC ?
                        <div className="job_wishes_specific_box job_wishes_week_days_specific_box">
                            <div className="job_wishes_specific_head">
                                <MatchInfoWrapper points={matchInfo ? matchInfo["availability_week_days"] : null}>
                                    <div className="text_light_2">{!editable ? locale.msgs.WEEK_DAYS : locale.msgs.WEEK_DAYS_SPECIFIC_2}</div>
                                </MatchInfoWrapper>
                            </div>
                                
                            <div className="week_days_specific_box">
                                <CheckBoxSquare readOnly={!editable} text={locale.msgs.MONDAY_SHORT}
                                        checked={userData["week_days_specific_monday"]}
                                        onChange={(checked) => setUserDataAndResetSaveState({...userData, "week_days_specific_monday": checked})}/>
                            
                                <CheckBoxSquare readOnly={!editable} text={locale.msgs.TUESDAY_SHORT}
                                        checked={userData["week_days_specific_tuesday"]}
                                        onChange={(checked) => setUserDataAndResetSaveState({...userData, "week_days_specific_tuesday": checked})}/>

                                <CheckBoxSquare readOnly={!editable} text={locale.msgs.WEDNESDAY_SHORT}
                                        checked={userData["week_days_specific_wednesday"]}
                                        onChange={(checked) => setUserDataAndResetSaveState({...userData, "week_days_specific_wednesday": checked})}/>

                                <CheckBoxSquare readOnly={!editable} text={locale.msgs.THURSDAY_SHORT}
                                        checked={userData["week_days_specific_thursday"]}
                                        onChange={(checked) => setUserDataAndResetSaveState({...userData, "week_days_specific_thursday": checked})}/>

                                <CheckBoxSquare readOnly={!editable} text={locale.msgs.FRIDAY_SHORT}
                                        checked={userData["week_days_specific_friday"]}
                                        onChange={(checked) => setUserDataAndResetSaveState({...userData, "week_days_specific_friday": checked})}/>
                            
                                <CheckBoxSquare readOnly={!editable} text={locale.msgs.SATURDAY_SHORT}
                                        checked={userData["week_days_specific_saturday"]}
                                        onChange={(checked) => setUserDataAndResetSaveState({...userData, "week_days_specific_saturday": checked})}/>

                                <CheckBoxSquare readOnly={!editable} text={locale.msgs.SUNDAY_SHORT}
                                        checked={userData["week_days_specific_sunday"]}
                                        onChange={(checked) => setUserDataAndResetSaveState({...userData, "week_days_specific_sunday": checked})}/>
                            </div>
                        </div>
                    :
                        null
                }


                {/* SKILLS */}

                <div className="job_wishes_specific_box">
                    <div className="job_wishes_specific_head">
                        <MatchInfoWrapper points={matchInfo ? matchInfo["skills"] : null}>
                            <div className="text_light_2">{userType === UserType.NANNY ? locale.msgs.SKILLS : locale.msgs.DESIRED_SKILLS}</div>
                        </MatchInfoWrapper>
                    </div>

                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.DRIVERS_LICENSE}
                            id={"skills_0_" + id} name={"skills_0_" + id}
                            checked={userData["drivers_license"]}
                            onChange={(e) => setUserDataAndResetSaveState({...userData, "drivers_license": e.target.checked})}/>
                
                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox"
                            text={userType === UserType.NANNY ? locale.msgs.DRIVE_FOREIGN_CAR : locale.msgs.DRIVE_FAMILY_CAR}
                            id={"skills_1_" + id} name={"skills_1_" + id}
                            checked={userData["drive_foreign_car"]}
                            onChange={(e) => setUserDataAndResetSaveState({...userData, "drive_foreign_car": e.target.checked})}/>

                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.HOMEWORK_SUPPORT}
                            id={"skills_2_" + id} name={"skills_2_" + id}
                            checked={userData["homework_support"]}
                            onChange={(e) => setUserDataAndResetSaveState({...userData, "homework_support": e.target.checked})}/>
                
                    { userType === UserType.FAMILY ?
                        <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.SPECIAL_CARE}
                                id={"skills_3_" + id} name={"skills_3_" + id}
                                checked={userData["special_care"]}
                                onChange={(e) => setUserDataAndResetSaveState({...userData, "special_care": e.target.checked})}/>
                    :
                        null
                    }

                    { userType === UserType.FAMILY && userData["special_care"] ?
                        <div className="special_care_text_box">
                            <TextInputMultiline readOnly={!editable}
                                    value={userData["special_care_text"]}
                                    onChange={(value) => setUserDataAndResetSaveState({...userData, "special_care_text": value})}
                                    showPlaceholderIfNotEmpty={false}
                                    height="8rem"
                                    recalcTrigger={recalcTrigger}
                                    maxLength={maxSpecialCareTextLength}
                                    tiny={true} />
                        </div>
                    :
                        null
                    }

                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.FIRST_AID_CURSE_FOR_CHILDREN}
                            id={"skills_4_" + id} name={"skills_4_" + id}
                            checked={userData["first_aid_course_for_children"]}
                            onChange={(e) => setUserDataAndResetSaveState({...userData, "first_aid_course_for_children": e.target.checked})}/>

                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox"
                            text={userType === UserType.NANNY ? locale.msgs.PETS_ARE_OK : locale.msgs.NO_PROBLEMS_WITH_PETS}
                            id={"skills_5_" + id} name={"skills_5_" + id}
                            checked={userData["pets_are_ok"]}
                            onChange={(e) => setUserDataAndResetSaveState({...userData, "pets_are_ok": e.target.checked})}/>
                
                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.MUSICALLY}
                            id={"skills_6_" + id} name={"skills_6_" + id}
                            checked={userData["musically"]}
                            onChange={(e) => setUserDataAndResetSaveState({...userData, "musically": e.target.checked})}/>

                    <RadioCheckButton tiny={true} readOnly={!editable} type="checkbox" text={locale.msgs.COOKING}
                            id={"skills_7_" + id} name={"skills_7_" + id}
                            checked={userData["cooking"]}
                            onChange={(e) => setUserDataAndResetSaveState({...userData, "cooking": e.target.checked})}/>
                </div>


                {/* LANGUAGES */}

                <div>
                    <div className="job_wishes_specific_head">
                        <MatchInfoWrapper points={matchInfo ? matchInfo["languages"] : null}>
                            <div className="text_light_2">{userType === UserType.NANNY ? locale.msgs.LANGUAGES : locale.msgs.DESIRED_LANGUAGES}</div>
                        </MatchInfoWrapper>
                    </div>

                    <LanguagesSelect readOnly={!editable} minimal={true} languages={userData["languages"]}
                            onLanguagesChange={(langs) => setUserDataAndResetSaveState({...userData, "languages": langs})}
                            maxLanguages={userType === UserType.FAMILY ? maxLanguagesFamily : maxLanguagesNanny } />
                </div>


                {/* COMMENT */}

                <div className="comment_box">
                    <div className="job_wishes_specific_head text_light_2">{management ? (userType === UserType.FAMILY ? locale.msgs.COMMENT_LABEL_MANAGE_FAM : locale.msgs.COMMENT_LABEL_MANAGE_MN) : locale.msgs.COMMENT_LABEL}</div>

                    <TextInputMultiline readOnly={!editable}
                            value={userData["comment"]}
                            onChange={(value) => setUserDataAndResetSaveState({...userData, "comment": value}) }
                            showPlaceholderIfNotEmpty={false}
                            height="8rem"
                            recalcTrigger={recalcTrigger}
                            maxLength={maxCustomerCommentLength}
                            tiny={true} />
                </div>

                { !readOnly ?
                    <SaveCancelBox editable={editable} savable={isSavable()} saving={saveState === LoadState.LOADING} saved={saveState === LoadState.LOAD_SUCCESS}
                            notSaved={saveState === LoadState.LOAD_ERROR} onSaveClick={handleSave} onCancelClick={handleCancel}
                            lockNavigation={lockNavigation}
                    />
                :
                    null
                }
            </div>
        </div>
    )
}

export const MatchInfoWrapper = ({children, points}) => {
    if(!points) {
        return children;
    }

    const displayPoints = Math.round(points[0] * 100);
    const displayMaxPoints = Math.round(points[1] * 100);

    // Full score
    let colorClass = " match_info_good";

    if(points[0] <= 0) {
        // Deal breaker
        colorClass = " match_info_break";
    }
    else if(points[0] !== points[1]){
        // Not the full score
        colorClass = " match_info_medium";
    }

    return (
        <div className={"match_info_wrapper"}>
            <div className={"match_info" + colorClass}>
                <div className="match_info_text">
                    {displayPoints.toString() + "/" + displayMaxPoints.toString()}
                </div>
            </div>

            {children}
        </div>
    )
}
